import {
  Accordion,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Textarea,
  useBreakpointValue,
  VStack,
} from '@chakra-ui/react';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { useFormItems } from '../../../../hooks/formItems';
import { Worker } from '../../../../models/gen/worker';
import { useMasterContext } from '../../../MasterProvider';
import { useSizeContext } from '../../../SizeProvider';
import BasicInformation from './Basic';
import FamilyInformation from './Family';
import HealthCheckupInformation from './HealthCheckup';
import HireAndSkillInformation from './HireAndSkill';
import InsuranceAndMutualAidInformation from './InsuranceAndMutualAid';

/**
 * 個人情報一覧プロパティ
 */
type UserInfoAccordionProps = {
  /**
   * 新規作成
   */
  create?: boolean;
  defaultIndex: number;
  isOutsourcer?: boolean;
  /**
   * 表示モード
   */
  mode: 'edit' | 'view';
};

/**
 * 個人情報状態
 */
export type UserInfoState = Worker & {
  /**
   * アカウント名
   */
  account?: string;
};

export const UserInfoAccordion: React.FunctionComponent<
  UserInfoAccordionProps
> = ({ defaultIndex, ...props }) => {
  const master = useMasterContext();
  const sizes = useSizeContext();
  const form = useFormContext<UserInfoState>();
  const { FormDatePicker, FormInput, FormSelect } = useFormItems(form);

  const emptyGridItemHidden = useBreakpointValue({ base: true, md: false });

  return (
    <VStack width={sizes.width.styledMessageList}>
      <Grid
        backgroundColor="white"
        borderColor="gray.300"
        borderWidth="1px"
        templateColumns={{ base: '1fr', md: '1fr 1fr' }}
        width="100%"
      >
        <GridItem margin={2}>
          <FormInput
            formControlProps={{ isRequired: true }}
            label="氏名"
            mode={props.mode}
            name="name"
          />
        </GridItem>
        <GridItem margin={2}>
          <FormInput
            formControlProps={{ isRequired: true }}
            label="氏名 (カナ)"
            mode={props.mode}
            name="nameK"
          />
        </GridItem>
        <GridItem margin={2}>
          <FormSelect
            formControlProps={{
              isRequired: true,
            }}
            label="性別"
            mode={props.mode}
            name="gender"
            options={[
              { value: '0', valueToDisplay: '不明' },
              { value: '1', valueToDisplay: '男性' },
              { value: '2', valueToDisplay: '女性' },
              { value: '9', valueToDisplay: 'その他' },
            ]}
          />
        </GridItem>
        <GridItem hidden={emptyGridItemHidden} />
        <GridItem hidden={!props.isOutsourcer} margin={2}>
          <FormDatePicker
            datePickerProps={{ showYearDropdown: true, dropdownMode: 'select' }}
            formControlProps={{ isRequired: true }}
            label="開始日"
            mode={props.mode}
            name="dateFrom"
            onClickClear={(name) => form.setValue(name, '')}
          />
        </GridItem>
        <GridItem hidden={!props.isOutsourcer} margin={2}>
          <FormDatePicker
            datePickerProps={{ showYearDropdown: true, dropdownMode: 'select' }}
            formControlProps={{ isRequired: true }}
            label="終了日"
            mode={props.mode}
            name="dateTo"
            onClickClear={(name) => form.setValue(name, '')}
          />
        </GridItem>
      </Grid>

      <Accordion
        width="100%"
        backgroundColor="white"
        borderWidth="2px"
        borderColor="gray.300"
        borderBottom="none"
        zIndex="1 !important"
        allowToggle
        defaultIndex={defaultIndex}
      >
        <BasicInformation
          isOutsourcer={props.isOutsourcer}
          master={master}
          mode={props.mode}
        />
        <HireAndSkillInformation
          isOutsourcer={props.isOutsourcer}
          master={master}
          mode={props.mode}
        />
        <FamilyInformation master={master} mode={props.mode} />
        <HealthCheckupInformation master={master} mode={props.mode} />
        <InsuranceAndMutualAidInformation master={master} mode={props.mode} />
      </Accordion>
      <FormControl backgroundColor="white" padding={2}>
        <FormLabel htmlFor="remarks" fontWeight="semibold">
          備考
        </FormLabel>
        <Controller
          name="workerRemarks"
          control={form.control}
          render={({ field }) => (
            <Textarea isReadOnly={props.mode === 'view'} {...field} />
          )}
        />
      </FormControl>
    </VStack>
  );
};
